import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import SegmentedTabs from "@/components/feature/SegmentedTabs";
import EpisodeCard from "@/components/feature/EpisodeCard";
import CharacterCard from "@/components/feature/CharacterCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/base/EmptyState";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { news } from "@/mocks/news";
import useDocumentMeta from "@/hooks/useDocumentMeta";

function Highlight({ text, keyword }: { text: string; keyword: string }) {
  if (!keyword) return <>{text}</>;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const parts = text.split(new RegExp(`(${escaped})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase() ? (
          <mark key={i} className="bg-accent-500/25 text-accent-300 rounded px-0.5">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

const hotLinks = [
  { label: "最终季大结局", to: "/episodes/s4e5" },
  { label: "核心角色艾琳·卡特", to: "/characters/elena" },
  { label: "反派维多利亚·斯特恩", to: "/characters/victoria" },
  { label: "三天空白深度解析", to: "/news/n2" },
  { label: "全四季章节目录", to: "/episodes" },
  { label: "最新资讯精选", to: "/news" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();
  const [keyword, setKeyword] = useState(q);
  const [tab, setTab] = useState<string | number>("全部");

  useDocumentMeta({
    title: "站内搜索 - 美国禁忌4",
    description: "在美国禁忌4站点内搜索剧集章节、角色档案与资讯内容，支持按分类查看搜索结果。",
  });

  const results = useMemo(() => {
    const kw = q.toLowerCase();
    if (!kw) return { episodes: [], characters: [], news: [] };

    const ep = episodes.filter((e) =>
      [e.title, e.summary, e.analysis, e.tags.join(" ")].join(" ").toLowerCase().includes(kw)
    );
    const ch = characters.filter((c) =>
      [c.name, c.enName, c.actor, c.bio, c.type, c.background.join(" ")].join(" ").toLowerCase().includes(kw)
    );
    const nw = news.filter((n) =>
      [n.title, n.summary, n.content.join(" "), n.tags.join(" "), n.category].join(" ").toLowerCase().includes(kw)
    );
    return { episodes: ep, characters: ch, news: nw };
  }, [q]);

  const counts = {
    episodes: results.episodes.length,
    characters: results.characters.length,
    news: results.news.length,
  };
  const totalCount = counts.episodes + counts.characters + counts.news;

  const tabs = [
    { value: "全部", label: `全部 (${totalCount})` },
    { value: "剧集章节", label: `剧集章节 (${counts.episodes})` },
    { value: "角色", label: `角色 (${counts.characters})` },
    { value: "资讯", label: `资讯 (${counts.news})` },
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = keyword.trim();
    setSearchParams(value ? { q: value } : {});
    setTab("全部");
  };

  const showEpisodes = tab === "全部" || tab === "剧集章节";
  const showCharacters = tab === "全部" || tab === "角色";
  const showNews = tab === "全部" || tab === "资讯";

  return (
    <div className="px-4 md:px-10 py-6 md:py-10">
      <PageHeader
        crumbs={[{ label: "首页", to: "/" }, { label: "搜索结果" }]}
        eyebrow="Search"
        title="站内搜索"
        description="输入关键词搜索剧集章节、角色档案与资讯内容，支持在结果中按分类切换查看。"
      />

      <form onSubmit={submit} className="mb-6 md:mb-8">
        <div className="relative max-w-2xl">
          <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500" />
          <input
            type="search"
            name="q"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索剧集章节、角色或资讯，例如：艾琳、大结局、解析"
            className="w-full rounded-md bg-background-100 border border-background-200 pl-11 pr-28 py-3 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:border-accent-500/60 transition-colors"
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md bg-accent-500 px-4 py-2 text-sm font-label text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>
      </form>

      {!q ? (
        <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
          <h2 className="font-heading text-lg text-foreground-950 mb-4">热门搜索推荐</h2>
          <div className="flex flex-wrap gap-3">
            {hotLinks.map((h) => (
              <Link
                key={h.to}
                to={h.to}
                className="inline-flex items-center gap-2 rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 hover:border-accent-500/50 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-fire-line text-accent-400" />
                {h.label}
              </Link>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <p className="text-sm text-foreground-500 mb-3">
              关键词 <span className="text-accent-400 font-medium">“{q}”</span> 共找到 {totalCount} 条结果
            </p>
            <SegmentedTabs ariaLabel="按结果分类筛选" options={tabs} value={tab} onChange={setTab} />
          </div>

          {totalCount === 0 ? (
            <EmptyState
              title="没有找到相关内容"
              description={`没有与“${q}”匹配的剧集章节、角色或资讯。试试更换关键词，或从下方热门内容开始浏览。`}
            />
          ) : (
            <div className="space-y-12">
              {showEpisodes && counts.episodes > 0 && (
                <section>
                  <h2 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-film-line text-accent-400" />
                    剧集章节 <span className="text-sm text-foreground-500 font-label">({counts.episodes})</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5">
                    {results.episodes.map((ep) => (
                      <EpisodeCard key={ep.id} episode={ep} />
                    ))}
                  </div>
                </section>
              )}

              {showCharacters && counts.characters > 0 && (
                <section>
                  <h2 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-user-star-line text-accent-400" />
                    角色 <span className="text-sm text-foreground-500 font-label">({counts.characters})</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 md:gap-5">
                    {results.characters.map((c) => (
                      <CharacterCard key={c.id} character={c} />
                    ))}
                  </div>
                </section>
              )}

              {showNews && counts.news > 0 && (
                <section>
                  <h2 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-newspaper-line text-accent-400" />
                    资讯 <span className="text-sm text-foreground-500 font-label">({counts.news})</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                    {results.news.map((n) => (
                      <NewsCard key={n.id} item={n} />
                    ))}
                  </div>
                </section>
              )}

              {tab !== "全部" && (
                <p className="text-sm text-foreground-500">
                  当前分类下显示{" "}
                  <Highlight
                    text={
                      tab === "剧集章节"
                        ? `${counts.episodes} 条结果`
                        : tab === "角色"
                        ? `${counts.characters} 条结果`
                        : `${counts.news} 条结果`
                    }
                    keyword={q}
                  />
                  ，可点击“全部”查看所有分类。
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}